import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $deleteTableColumnAtSelection,
  $getNodeTriplet,
  $getTableColumnIndexFromTableCellNode,
  $insertTableColumnAtSelection,
  $isTableCellNode,
  $isTableSelection,
  TableCellNode,
} from "@lexical/table";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import PaddingIcon from "@mui/icons-material/Padding";
import Palette from "@mui/icons-material/Palette";
import { Box, Stack } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { $getSelection, $isRangeSelection } from "lexical";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../generic/components/colour-picker/colour-picker";
import { $isStylableTableCellMui, StylableTableCellMui } from "../../nodes/stylable-table-cell-mui";
import {
  INSERT_STYLEABLE_TABLE_CELL_COMMAND,
  SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
} from "../../types/commands";
import { useStyleableColumns } from "../../utils/use-styleable-columns";
import { ToolbarIconButton } from "../toolbar/toolbar-icon-button";
import { TableColumnPopoverSlider } from "./table-column-popover-slider";
import { TableColumnPopoverToggle } from "./table-column-popover-toggle";

export const TableColumnPopoverMenu: FC<{
  tableCellNode: StylableTableCellMui | TableCellNode;
  onClick: () => void;
}> = ({ tableCellNode, onClick }) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const stylableColumns = useStyleableColumns();
  const popover = usePopover<HTMLButtonElement>();
  const [colour, setTempColour] = useState<string>("");
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const [paddingMenuOpen, setPaddingMenuOpen] = useState(false);

  const handleDelete = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) && !$isTableSelection(selection)) {
        onClick();
        return;
      }

      const anchorNode = selection.anchor.getNode();
      const [cell, , table] = $getNodeTriplet(anchorNode);
      if (!$isTableCellNode(cell)) {
        onClick();
        return;
      }

      const widths = table.getColWidths();
      if (!widths) {
        $deleteTableColumnAtSelection();
        onClick();
        return;
      }

      const columnIndex = $getTableColumnIndexFromTableCellNode(cell);
      const widthOfDeletedColumn = widths[columnIndex];
      if (widthOfDeletedColumn === undefined) {
        $deleteTableColumnAtSelection();
        onClick();
        return;
      }

      $deleteTableColumnAtSelection();

      // Proportionally redistribute the deleted column's width
      const newWidths = table.getColWidths() ?? [];
      const sumOfRemainingWidths = newWidths.reduce((a, b) => a + b, 0);
      const redistributedWidths = newWidths.map(
        (w) => w + (w / sumOfRemainingWidths) * widthOfDeletedColumn,
      );

      table.setColWidths(redistributedWidths);
      onClick();
    });
  }, [editor, onClick]);

  const handleInsert = useCallback(
    (insertAfter: boolean) => {
      if (!stylableColumns.enabled) {
        editor.update(() => {
          $insertTableColumnAtSelection(insertAfter);
          onClick();
        });
      } else {
        editor.dispatchCommand(INSERT_STYLEABLE_TABLE_CELL_COMMAND, { insertAfter });
        onClick();
      }
    },
    [editor, onClick, stylableColumns],
  );

  const setColour = useCallback(
    (c: string, skipAddingToHistoryStack?: boolean) => {
      editor.update(
        () => tableCellNode.setBackgroundColor(c),
        skipAddingToHistoryStack ? { tag: "historic" } : {},
      );
      setTempColour(c || "");
    },
    [editor, tableCellNode],
  );

  useEffect(() => {
    editor.read(() => {
      const cellBackgroundColor = tableCellNode.getBackgroundColor() ?? "";
      if (cellBackgroundColor !== colour) {
        setTempColour(cellBackgroundColor);
      }
    });
  }, [editor, tableCellNode, colour]);

  const styleableTableCellMui = useMemo(() => {
    if (stylableColumns.enabled && $isStylableTableCellMui(tableCellNode)) {
      return tableCellNode;
    }
    return null;
  }, [tableCellNode, stylableColumns]);

  return (
    <>
      {/* eslint-disable-next-line react-hooks/refs */}
      <Box display="flex" flexDirection="column" ref={popover.anchorRef}>
        <ToolbarIconButton
          data-analytics-id="lexical-table-menu-delete-column"
          disabled={!editor._editable}
          label={"Delete column"}
          icon={<DeleteIcon data-testid="DeleteIcon" fontSize="small" />}
          data-testid="table-menu-delete-column"
          aria-label="delete-column"
          onClick={handleDelete}
        />
        <ToolbarIconButton
          data-analytics-id="lexical-table-menu-insert-left"
          disabled={!editor._editable}
          label={"Insert column left"}
          icon={<KeyboardArrowLeft fontSize="small" />}
          data-testid="table-menu-insert-left"
          aria-label="insert-column-left"
          onClick={() => handleInsert(false)}
        />
        <ToolbarIconButton
          data-analytics-id="lexical-table-menu-insert-right"
          disabled={!editor._editable}
          label={"Insert column right"}
          icon={<KeyboardArrowRight fontSize="small" />}
          data-testid="table-menu-insert-right"
          aria-label="insert-column-right"
          onClick={() => handleInsert(true)}
        />
        <ToolbarIconButton
          data-analytics-id="lexical-table-menu-cell-background-colour"
          disabled={!editor._editable}
          label={"Background color"}
          icon={<Palette fontSize="small" />}
          data-testid="table-menu-colour"
          aria-label="change-cell-background-colour"
          // eslint-disable-next-line react-hooks/refs
          onClick={popover.handleToggle}
        />
        {styleableTableCellMui && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ToolbarIconButton
              data-analytics-id="lexical-table-menu-cell-padding-menu"
              disabled={!editor._editable}
              label={"Column Padding"}
              icon={<PaddingIcon fontSize="small" />}
              data-testid="table-menu-padding"
              aria-label="change-cell-padding"
              onClick={() => {
                setPaddingMenuOpen((prev) => !prev);
              }}
            />
            <KeyboardArrowDownIcon
              data-analytics-id="lexical-table-menu-cell-padding-menu"
              fontSize="small"
              onClick={() => {
                setPaddingMenuOpen((prev) => !prev);
              }}
              sx={{
                transform: paddingMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </Stack>
        )}
        <Collapse in={paddingMenuOpen}>
          <Stack direction={"column"} spacing={1} alignItems="start" sx={{ p: 1 }}>
            {styleableTableCellMui && (
              <TableColumnPopoverToggle
                value={styleableTableCellMui.getIsIndividualPadding()}
                onChange={(isChecked) => {
                  if (isChecked) {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                    });
                  } else {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: false,
                    });
                  }
                }}
                name="individual-padding"
                label={t("Edit individual paddings")}
              />
            )}
            {styleableTableCellMui && !styleableTableCellMui?.getIsIndividualPadding() && (
              <TableColumnPopoverSlider
                value={styleableTableCellMui.getCommonPadding()}
                onChange={(value) => {
                  editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                    nodeKey: tableCellNode.getKey(),
                    individualPadding: false,
                    common: value,
                  });
                }}
                sliderProps={{
                  max: 50,
                  step: 1,
                }}
                label={t("Column Padding")}
                name="common-padding"
              />
            )}
            {styleableTableCellMui && styleableTableCellMui.getIsIndividualPadding() && (
              <>
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingTop()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      top: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label={t("Column Padding Top")}
                  name="padding-top"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingBottom()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      bottom: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label={t("Column Padding Bottom")}
                  name="padding-bottom"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingLeft()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      left: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label={t("Column Padding Left")}
                  name="padding-left"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingRight()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      right: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label={t("Column Padding Right")}
                  name="padding-right"
                />
              </>
            )}
          </Stack>
        </Collapse>
      </Box>
      <ColourPicker
        // eslint-disable-next-line react-hooks/refs
        popover={popover}
        colour={colour}
        onInput={setColour}
        onChange={setColour}
        inputRef={popoverInputRef}
      />
    </>
  );
};
